import { useState } from 'react';
import { useIntl } from 'react-intl';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';
import { Dialog } from '@packages/shared';
import { useConfig, useCookieConsent } from '@packages/utilities';
import { Video32 } from '@packages/themes/icons';
import { useTracking } from '@packages/tracking';
import { addCliplisterScript, useRendered } from './helper';
import { messagesCliplister } from './messages';
import { CliplisterVideo } from './CliplisterVideo';
import { ImageGalleryCallToActionButton } from '../ImageGalleryCallToActionButton';

const CliplisterFragment = graphql(/* GraphQL */ `
  fragment CliplisterFragment on Product {
    orderNumber
    cliplister(lang: $lang) @skip(if: $skipCliplister) {
      url
    }
  }
`);

/**
 * <p>
 * This component is used to show product videos from cliplister.
 * </p>
 * <p>
 * It originally shows an icon. After it is clicked a `Dialog` opens, which shows a product preview image.
 * After the image is clicked, the player is appended and starts playing the product video.
 *
 * This components needs to have Cookie Consent 4 (marketing) in order to be displayed.
 * <a href="/story/docs-storybooksetup--page">Here</a> you can see how you can simulate the consent in storybook.
 * </p>
 *
 */

export const Cliplister = ({
  productData,
}: {
  productData: FragmentType<typeof CliplisterFragment>;
}) => {
  const { orderNumber, cliplister } = getFragmentData(CliplisterFragment, productData);
  const { formatMessage } = useIntl();
  const {
    product: {
      cliplister: { scriptUrl, consentGroup, enabled, customerId },
    },
  } = useConfig();
  const rendered = useRendered();
  const [openVideo, setOpenVideo] = useState(false);
  const isCookieAllowed = useCookieConsent(consentGroup);
  const dispatchGTMEvent = useTracking();

  if (!rendered || !enabled || !cliplister?.url || !isCookieAllowed) return null;

  addCliplisterScript(scriptUrl);

  const openModalHandler = () => {
    dispatchGTMEvent({
      event: 'ga_event',
      eventAction: 'viewVideo',
      eventCategory: 'cliplister',
      eventLabel: 'detailview',
      eventValue: 'click',
    });
    setOpenVideo(true);
  };

  return (
    <>
      <ImageGalleryCallToActionButton onClick={openModalHandler}>
        <Video32 sx={{ fill: (theme) => theme.palette.text.dark }} />
      </ImageGalleryCallToActionButton>
      <Dialog
        open={openVideo}
        handleClose={() => {
          setOpenVideo(false);
        }}
        title={formatMessage(messagesCliplister.cliplisterModalTitle)}
        dialogContent={<CliplisterVideo orderNumber={orderNumber} customerId={customerId} />}
      />
    </>
  );
};
